import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, Space } from 'antd';
import { DocumentList } from './components/DocumentList';
import {
  DocumentationProvider,
  useDocumentationStore
} from './documentation-store';
import { EditLayout } from '../../../components/layout/layout/EditLayout';
import { FolderOutlined, SyncOutlined } from '@ant-design/icons';
import {
  IDocumentTemplate,
  IDocumentTemplateCategory,
  IDocumentTemplateCourse
} from 'interfaces/documentation/document-template';
import { IDocumentation } from 'interfaces/documentation/documentations';
import { ModalApproveDocument } from './components/ModalApproveDocument';
import { ModalRejectDocument } from './components/ModalRejectDocument';
import { ModalRejectDocumentation } from './components/ModalRejectDocumentation';
import { ModalSetKind } from './components/ModalSetKind';
import { ModalShowDocument } from './components/ModalShowDocument';
import { PageHeader } from '../../../components/professor_area/PageHeader';
import { StudentInformation } from './components/StudentInformation';
import { SupplementaryDocuments } from './components/SupplementaryDocuments';
import { cinStatus } from './components/document-requirement';
import { useCan, useCreate, useList, useModal } from '@refinedev/core';
import { useForm } from '@refinedev/antd';

const CATEGORY_MAP: Record<IDocumentTemplateCategory, string> = {
  academic: 'Documentação acadêmica',
  identification: 'Documentação de identificação',
  supplementary: 'Documentação complementar'
};

type DocumentationData = Record<IDocumentTemplateCategory, DocumentationMap>;

interface DocumentationMap {
  title: string;
  templates: IDocumentTemplate[];
}

export const DocumentationEdit = () => {
  return (
    <DocumentationProvider>
      <DocumentationEditContent />
    </DocumentationProvider>
  );
};

export const DocumentationEditContent = () => {
  const [documentTemplates, setDocumentTemplates] = useState<
    IDocumentTemplate[]
  >([]);
  const setQueryHandler = useDocumentationStore(s => s.setQueryHandler);
  const { saveButtonProps, queryResult } = useForm<IDocumentation>({
    redirect: 'edit'
  });

  const { mutateAsync } = useCreate();
  const { data: canListDocumentTemplates } = useCan({
    resource: 'document_templates',
    action: 'list'
  });
  const { data: canListTemplateCourses } = useCan({
    resource: 'document_template_courses',
    action: 'list'
  });

  const formParent = useMemo(
    () => queryResult?.data?.data.parent,
    [queryResult]
  );

  const { data: supplementary } = useList<IDocumentTemplate>({
    resource: 'documents/templates',
    queryOptions: {
      enabled: Boolean(canListDocumentTemplates?.can)
    },

    filters: [
      {
        field: 'filter[supplementary_by_course_id]',
        operator: 'eq',
        value: formParent?.course.id
      }
    ],

    pagination: {
      pageSize: 99
    }
  });

  const { data: templateCourses } = useList<IDocumentTemplateCourse>({
    resource: 'documents/template_courses',
    queryOptions: {
      enabled: Boolean(canListTemplateCourses?.can)
    },

    filters: [
      {
        field: 'filter[by_course_id]',
        operator: 'eq',
        value: formParent?.course.id
      }
    ],

    pagination: {
      pageSize: 99
    }
  });

  const { data: statics } = useList<IDocumentTemplate>({
    resource: 'documents/templates',
    queryOptions: {
      enabled: Boolean(canListDocumentTemplates?.can)
    },

    filters: [
      {
        field: 'filter[by_category][]',
        operator: 'eq',
        value: ['academic', 'identification']
      },
      {
        field: 'filter[by_course_id]',
        operator: 'eq',
        value: formParent?.course.id
      }
    ],

    pagination: {
      pageSize: 99
    }
  });

  useEffect(() => {
    const handleQueryRefetch = async () => {
      await queryResult?.refetch();
    };

    setQueryHandler(handleQueryRefetch);
  }, [setQueryHandler, queryResult]);

  useEffect(() => {
    setDocumentTemplates([
      ...(statics?.data || []),
      ...(supplementary?.data || [])
    ]);
  }, [statics, supplementary]);

  const documents = useMemo(
    () => queryResult?.data?.data?.documents || [],
    [queryResult]
  );

  // Sobre todos os documentos e templates, não por categoria: a CIN que
  // dispensa o CPF é um documento de outro template.
  const documentsCinStatus = useMemo(
    () => cinStatus(documents, documentTemplates),
    [documents, documentTemplates]
  );

  const documentationByCategory = useMemo(() => {
    const categorizedDocuments = {} as DocumentationData;

    for (const template of documentTemplates) {
      const { category } = template;

      if (!categorizedDocuments[category]) {
        categorizedDocuments[category] = {
          title: CATEGORY_MAP[category],
          templates: documentTemplates.filter(
            temp => temp.category === category
          )
        };
      }
    }
    return categorizedDocuments;
  }, [documentTemplates]);

  const rejectDocumentationModal = useModal();

  const refreshDocumentation = async () => {
    const documentationId = queryResult?.data?.data.id;

    if (!documentationId) {
      return;
    }

    return await mutateAsync({
      resource: `documentations/${documentationId}/refresh`,
      values: {},
      successNotification: () => ({
        type: 'success',
        description: 'Sucesso!',
        message: 'Documentação adicionada para reavaliação!'
      }),
      errorNotification: () => ({
        type: 'error',
        description: 'Ops!',
        message:
          'Não foi possível adicionar a documentação para reavaliação. Tente novamente.'
      })
    }).catch(error => {
      // eslint-disable-next-line no-console
      console.error(error);
    });
  };

  return (
    <EditLayout
      saveButtonProps={saveButtonProps}
      footerButtonProps={{ hidden: true }}
    >
      <PageHeader
        title="Validação de documentação"
        icon={FolderOutlined}
        actions={
          <Button
            size="small"
            icon={<SyncOutlined />}
            onClick={() => refreshDocumentation()}
          >
            Reavaliar documentação
          </Button>
        }
      />
      <Card>
        <StudentInformation
          user={formParent?.user}
          course={formParent?.course}
          enrollment={formParent}
          status={queryResult?.data?.data.status || ''}
        />
        <Space direction="vertical" size="large" className="w-full">
          {Object.entries(documentationByCategory).map(([_, data], index) => (
            <DocumentList
              key={data.title + index}
              title={data.title}
              templates={data.templates}
              templateCourses={templateCourses?.data ?? []}
              documents={documents}
              cinStatus={documentsCinStatus}
            />
          ))}

          <SupplementaryDocuments
            title="Documentação não categorizada"
            documents={documents.filter(
              ({ template_id, status }) =>
                !template_id && !['draft', 'deleted'].includes(status)
            )}
          />
        </Space>
        <ModalSetKind templates={documentTemplates} />
        <ModalApproveDocument />
        <ModalRejectDocument />
        <ModalShowDocument />
        {rejectDocumentationModal.visible && (
          <ModalRejectDocumentation
            documentationId={queryResult?.data?.data.id}
            modal={rejectDocumentationModal}
          />
        )}
      </Card>
    </EditLayout>
  );
};
